import React from 'react';
import '../../styles/components/AsperaContextMenu.scss';
import { SubManager } from '../../classes/SubManager';
import { fromEvent } from 'rxjs';
import { AsperaError } from '../../classes/AsperaError';
import { containsDomChild } from '../../helpers/starterHelpers';
import { language } from '../../services/LanguageService';
/**
 * React Component
 *
 * The AsperaContextMenu provides the styled Right Click menu and renders based on a mouse coordinate.
 * This component closes on click outside, ESC keypress, Resize or Scroll events
 * This component calculates safest way to open (right/left and up/down) based on distance to the viewport edges.
 *
 * @example
 * ```javascript
 * <AsperaContextMenu menuOptions{[]} itemSelected={[]} />
 * ```
 */
export class AsperaContextMenu extends React.Component {
    constructor() {
        super(...arguments);
        this.subManager = new SubManager();
        /** The main class to use for the wrapper (used for event listeners as well) */
        this.mainClass = 'aspera-context-menu';
        /** The width of the menu. This is 12rem */
        this.menuWidth = 192;
    }
    handleClick(item) {
        const { itemsSelected, onClose } = this.props;
        if (itemsSelected.length === 1) {
            item.onClick(itemsSelected[0]);
        }
        else {
            item.onClick(itemsSelected);
        }
        onClose();
    }
    getInfoItem() {
        const { itemsSelected } = this.props;
        if (itemsSelected.length === 1)
            return undefined;
        return (React.createElement("li", { className: "cds--overflow-menu-options__option context-info-text" }, language.get('table.itemItemsSelected', [itemsSelected.length])));
    }
    getX() {
        const { openCoordinate } = this.props;
        const pageSafeMax = (document.body.offsetWidth || 0) - 200;
        if (openCoordinate.x > pageSafeMax) {
            return openCoordinate.x - this.menuWidth;
        }
        return openCoordinate.x;
    }
    getY() {
        const { openCoordinate, menuOptions } = this.props;
        let result = openCoordinate.y;
        /** Calculate total height with 40px buffer (deals with margin/padding and stuff) */
        const totalHeight = ((this.getInfoItem() ? 1 : 0) + menuOptions.length) * 40;
        const pageHeight = window.innerHeight || 0;
        const pageSafeMax = pageHeight - (totalHeight + 40);
        if (result > pageSafeMax) {
            result = result - totalHeight;
        }
        if (((totalHeight + result) > pageHeight)) {
            result = pageHeight - totalHeight;
        }
        if (result < 0)
            result = 0;
        return result;
    }
    componentDidMount() {
        const { onClose } = this.props;
        this.subManager.setSubscription(fromEvent(document, 'click').subscribe({
            next: event => {
                if (!containsDomChild(`.${this.mainClass}`, event.target))
                    onClose();
            },
            error: error => {
                new AsperaError('AsperaContextMenu: cannot setup click event on document', error);
            },
        }));
        this.subManager.setSubscription(fromEvent(document, 'scroll').subscribe({
            next: () => {
                onClose();
            },
            error: error => {
                new AsperaError('AsperaContextMenu: cannot setup scroll event on document', error);
            },
        }));
        this.subManager.setSubscription(fromEvent(window, 'resize').subscribe({
            next: () => {
                onClose();
            },
            error: error => {
                new AsperaError('AsperaContextMenu: cannot setup resize event on window', error);
            },
        }));
        this.subManager.setSubscription(fromEvent(document, 'keyup').subscribe({
            next: event => {
                if (event.key === 'Escape')
                    onClose();
            },
            error: error => {
                new AsperaError('AsperaContextMenu: cannot setup keyboard event on document', error);
            },
        }));
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    render() {
        const { className, menuOptions } = this.props;
        const style = {
            left: `${this.getX()}px`,
            top: `${this.getY()}px`,
        };
        if (!menuOptions.length)
            return null;
        return (React.createElement("div", { className: `${this.mainClass} ${className ? className : ''}`, style: style },
            React.createElement("ul", { className: "overflow-list cds--overflow-menu-options cds--overflow-menu-options--open", style: { width: `${this.menuWidth}px` } },
                this.getInfoItem(),
                menuOptions.map((option, index) => {
                    return (React.createElement("li", { className: `cds--overflow-menu-options__option ${option.disabled ? 'cds--overflow-menu-options__option--disabled' : ''}`, key: index },
                        React.createElement("button", { className: "cds--overflow-menu-options__btn", style: { maxWidth: `${this.menuWidth}px` }, type: "button", disabled: option.disabled || false, "data-auto": `context-menu-item-${option.name}`, onClick: () => {
                                this.handleClick(option);
                            } }, option.name)));
                }))));
    }
}
